<template>
  <article class="able-action" :class="{ 'able-action--highlight': highlight }">
    <div class="able-action__icon-wrap" aria-hidden="true">
      <i :class="icon" />
    </div>
    <div class="able-action__body">
      <p v-if="kicker" class="able-action__kicker">{{ kicker }}</p>
      <h3 class="able-action__title">{{ title }}</h3>
      <p class="able-action__lead">{{ lead }}</p>
      <ul v-if="bullets.length" class="able-action__list">
        <li v-for="(line, idx) in bullets" :key="idx">
          <i class="fa-solid fa-circle-check" aria-hidden="true" />
          {{ line }}
        </li>
      </ul>
      <div class="able-action__buttons">
        <router-link v-if="primaryTo" :to="primaryTo" class="btn primary">
          <i v-if="primaryIcon" :class="primaryIcon" aria-hidden="true" />
          {{ primaryLabel }}
        </router-link>
        <button v-else type="button" class="btn primary" @click="$emit('primary')">
          <i v-if="primaryIcon" :class="primaryIcon" aria-hidden="true" />
          {{ primaryLabel }}
        </button>
        <button
          v-if="secondaryLabel"
          type="button"
          class="btn outline-dark"
          @click="$emit('secondary')"
        >
          <i v-if="secondaryIcon" :class="secondaryIcon" aria-hidden="true" />
          {{ secondaryLabel }}
        </button>
      </div>
      <p v-if="footnote" class="able-action__footnote">{{ footnote }}</p>
    </div>
  </article>
</template>

<script>
export default {
  name: "AbleActionPanel",
  props: {
    kicker: { type: String, default: "" },
    icon: { type: String, required: true },
    title: { type: String, required: true },
    lead: { type: String, required: true },
    bullets: { type: Array, default: () => [] },
    primaryLabel: { type: String, required: true },
    primaryIcon: { type: String, default: "" },
    primaryTo: { type: [String, Object], default: "" },
    secondaryLabel: { type: String, default: "" },
    secondaryIcon: { type: String, default: "" },
    footnote: { type: String, default: "" },
    highlight: { type: Boolean, default: false },
  },
  emits: ["primary", "secondary"],
};
</script>

<style scoped>
.able-action {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: clamp(16px, 3vw, 28px);
  align-items: start;
  padding: clamp(22px, 4vw, 32px);
  border-radius: 18px;
  background: var(--background-color2);
  border: 1px solid var(--border-color-hover1, #e5e7eb);
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.06);
}

.able-action--highlight {
  border-color: var(--border-color-hover);
  box-shadow: 0 10px 32px rgba(0, 0, 0, 0.1);
}

.able-action__icon-wrap {
  width: clamp(52px, 10vw, 64px);
  height: clamp(52px, 10vw, 64px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.04);
  color: var(--border-color-hover);
  font-size: 1.6rem;
  flex-shrink: 0;
}

.able-action__kicker {
  margin: 0 0 6px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--border-color-hover);
}

.able-action__title {
  margin: 0 0 10px;
  font-size: clamp(1.15rem, 2.5vw, 1.45rem);
  font-weight: 800;
  color: var(--text-color1);
}

.able-action__lead {
  margin: 0 0 16px;
  font-size: 0.98rem;
  line-height: 1.6;
  color: var(--text-color1);
  opacity: 0.88;
}

.able-action__list {
  margin: 0 0 20px;
  padding: 0;
  list-style: none;
  display: grid;
  gap: 8px;
}

.able-action__list li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-size: 0.92rem;
  line-height: 1.45;
  color: var(--text-color1);
  opacity: 0.9;
}

.able-action__list i {
  color: var(--border-color-hover);
  margin-top: 3px;
  flex-shrink: 0;
}

.able-action__buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 32px;
  cursor: pointer;
  border: none;
  font-weight: 600;
  font-size: clamp(0.9rem, 1.6vw, 1rem);
  text-decoration: none;
  transition: 0.3s ease;
  font-family: inherit;
}

.btn.primary {
  background: var(--border-color-hover);
  color: var(--text-color3);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.btn.primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
}

.btn.outline-dark {
  background: transparent;
  border: 2px solid var(--border-color-hover);
  color: var(--text-color1);
}

.btn.outline-dark:hover {
  background: rgba(0, 0, 0, 0.04);
  transform: translateY(-2px);
}

.able-action__footnote {
  margin: 14px 0 0;
  font-size: 0.82rem;
  line-height: 1.45;
  color: var(--text-color1);
  opacity: 0.7;
}

@media (max-width: 640px) {
  .able-action {
    grid-template-columns: 1fr;
  }

  .able-action__buttons .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
